import { AddIcon, EditIcon } from '@chakra-ui/icons';
import {
    Flex,
    FormControl,
    FormLabel,
    HStack,
    useDisclosure,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsList } from 'react-icons/bs';
import { LuMinus } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useRegistrarFilter } from 'pages/resources/aors/hooks/useRegistrarFilter';
import useGetTRSRegistrations from './queries/useGetTRSRegistrations';
import { useRegistrarsMini } from './queries/useRegistrarsMini';

import { Button, Text } from '@/components/v4';
import { dropDownFormStyle } from 'components/forms/new/formStyles';
import { Error } from 'components/index';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import Tooltip from 'components/v4/Tooltip';
import FeatureDisabledPage from 'pages/resources/feature-disabled-page';
import StatusCellRenderer from './cellRenderer/StatusCellRenderer';
import LogsModal from './LogsModal';
import AORStatuses from './utils/statuses';
import ActionMenu from 'components/presentational/ActionMenu';
import NumberCellRenderer from 'pages/telephony/numbers/edit/cellRenderer/NumberCellRenderer';

import { tooltipChildrenStyles } from './constants';
import { renderTelephoneNumber } from 'utils/utils';

export const EnabledCellRenderer = ({ value }) => {
    return (
        <Flex alignItems="center" justify="center" height="100%">
            <Text>{value ? 'Enabled' : 'Disabled'}</Text>
        </Flex>
    );
};

// NOTE: TRS Registrations component (AORs)
export default function AORsIndex() {
    const gridRef = useRef();
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { restError } = useSelector((state) => state.modal);
    const isNotEnabled = restError?.includes('not enabled') || false;

    // Logs modal state
    const [selectedAORForLogs, setSelectedAORForLogs] = useState(null);
    const [extensionName, setExtensionName] = useState('');
    const {
        isOpen: isOpenLogsModal,
        onOpen: onOpenLogsModal,
        onClose: onCloseLogsModal,
    } = useDisclosure();

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const {
        data: registrars = [],
        isLoading: registrarsLoading,
        error: registrarsError,
    } = useRegistrarsMini(currentCompany);

    const { selectedRegistrarId, setRegistrarFilter } =
        useRegistrarFilter(registrars);

    const defaultLocationSearch = selectedRegistrarId
        ? `?registrar=${selectedRegistrarId}`
        : '';
    const selectedRegistrarValue = registrars?.find(
        (r) => r.id === selectedRegistrarId,
    );
    const selectedRegistrarPlaceholder = registrarsLoading
        ? 'Loading registrars...'
        : 'Select registrar';
    const isSelectedDisabled = registrarsLoading || !registrars.length;
    const tooltipSelectLabel = isSelectedDisabled
        ? 'There is no Registrar available for this Company'
        : '';
    const handleRegistrarChange = (value) => {
        setRegistrarFilter(value?.id);
    };

    const handleEditAOR = useCallback(
        (aorData) => {
            navigate(`/aors/edit/${aorData.id}${location.search}`);
        },
        [navigate],
    );

    const handleShowLogs = useCallback(
        (aor) => {
            setSelectedAORForLogs(aor.id);
            setExtensionName(aor.extension);
            onOpenLogsModal();
        },
        [onOpenLogsModal],
    );

    const handleCloseLogsModal = () => {
        onCloseLogsModal();
        setSelectedAORForLogs(null);
    };

    const handleAddAOR = () => {
        navigate(`/aors/add${location.search || defaultLocationSearch}`);
    };

    const handleCsvUpload = () => {
        navigate(
            `/aors/csv${selectedRegistrarId ? `?registrar=${selectedRegistrarId}` : ''}`,
        );
    };

    // (3119): only the rows mirrored into local state need clearing here —
    // selectedRegistrar* are derived from registrars/URL and update on their own.
    useEffect(() => {
        setTableData({ columns: [], data: [] });
    }, [currentCompany]);

    const baseColDefs = useMemo(
        () => [
            {
                headerName: 'Extension',
                field: 'extension',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
            {
                headerName: 'Registrar',
                field: 'registrar.name',
                cellRenderer: CopyCellRenderer,
                flex: 1.2,
                minWidth: 150,
                sortable: true,
            },
            {
                headerName: 'Assigned Number',
                field: 'assignedNumber',
                cellRenderer: (params) => {
                    return params?.value ? (
                        <NumberCellRenderer
                            value={params.value}
                            valueFormatted={renderTelephoneNumber(
                                params.data,
                                'assignedNumber',
                            )}
                        />
                    ) : (
                        <LuMinus fontSize="large" />
                    );
                },
                flex: 1.2,
                minWidth: 150,
                sortable: true,
            },
            {
                headerName: 'Status',
                field: 'status',
                cellRenderer: (params) =>
                    StatusCellRenderer({
                        value: params.value,
                        // Handle backend typo: try both statusMessage and statusMesssage
                        statusMessage:
                            params.data.statusMessage ||
                            params.data.statusMesssage,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    // Priority logic: backend message first, then frontend mapping
                    const backendMessage =
                        params.data.statusMessage || params.data.statusMesssage;
                    if (backendMessage && backendMessage.trim() !== '') {
                        return backendMessage;
                    }

                    const statusConfig =
                        AORStatuses[params?.value] || AORStatuses.default;
                    return statusConfig.displayedName;
                },
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        if (!isSelectedAll) {
                            const statusConfig =
                                AORStatuses[params?.value] ||
                                AORStatuses.default;
                            return (
                                <Text isTruncated>
                                    {statusConfig.displayedName}
                                </Text>
                            );
                        } else {
                            return '(Select All)';
                        }
                    },
                },
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
            {
                headerName: 'Active',
                field: 'isEnabled',
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.value,
                        darkMode,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.value ? 'Enabled' : 'Disabled';
                },
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? 'Enabled' : 'Disabled';
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
                flex: 1,
                minWidth: 100,
                sortable: true,
            },
            {
                headerName: 'Advanced Transfer',
                field: 'useAdvancedTransfer',
                cellRenderer: (params) =>
                    EnabledCellRenderer({
                        value: params.value,
                        darkMode,
                    }),
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                valueFormatter: (params) => {
                    return params.value ? 'Enabled' : 'Disabled';
                },
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? 'Enabled' : 'Disabled';
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
                flex: 1,
                minWidth: 140,
                sortable: true,
            },
            {
                headerName: 'Authentication',
                field: 'username',
                cellRenderer: (params) => {
                    const hasCustomAuth = !!(
                        params.data.username &&
                        params.data.password &&
                        params.data.username.trim() !== '' &&
                        params.data.password.trim() !== ''
                    );

                    return EnabledCellRenderer({
                        value: hasCustomAuth,
                        darkMode,
                    });
                },
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }),
                filter: 'agSetColumnFilter',
                filterParams: {
                    cellRenderer: (params) => {
                        console.log('filter column name: ', params);
                        const isSelectedAll = params?.value === '(Select All)';
                        if (isSelectedAll) {
                            return '(Select All)';
                        }

                        // params.value here is the boolean from valueGetter
                        const value = params.value ? 'Enabled' : 'Disabled';
                        return value;
                    },
                },
                flex: 1,
                minWidth: 120,
                sortable: true,
            },
        ],
        [darkMode],
    );

    // Custom icon component for Show Logs action
    const LogsIcon = () => <BsList style={{ fontSize: '1rem' }} />;

    // Actions column definition
    const actionsColDef = useMemo(
        () => ({
            lockPosition: 'right',
            headerName: 'Action',
            maxWidth: 100,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            sortable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => {
                const actionItems = [
                    {
                        label: 'Edit',
                        onClick: () => handleEditAOR(params.data),
                        icon: EditIcon,
                    },
                    {
                        label: 'Show Logs',
                        onClick: () => handleShowLogs(params.data),
                        icon: LogsIcon,
                    },
                ];

                return <ActionMenu actionItems={actionItems} />;
            },
        }),
        [handleEditAOR, handleShowLogs],
    );

    const baseColumns = useMemo(() => {
        const columns = [...baseColDefs];
        columns.push(actionsColDef);
        return columns;
    }, [baseColDefs, actionsColDef]);

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching, error } = useGetTRSRegistrations(
        setTableData,
        baseColumns,
        selectedRegistrarValue?.id || null,
    );

    // The registrar list has to resolve before registrations can be requested,
    // so treat it as part of the same in-flight window.
    const isFetchingRegistrations = isFetching || registrarsLoading;

    const titleName = 'TRS Registrations';

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    if (isNotEnabled || error || registrarsError) {
        return <FeatureDisabledPage />;
    }

    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                {titleName}
            </Text>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isFetching={isFetchingRegistrations}
                isRefetching={isRefetching}
                titleName={titleName}
                refetch={refetch}
                isRefreshDisabled={isSelectedDisabled}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            minW="100px"
                            onClick={handleCsvUpload}
                            size="sm">
                            CSV Update
                        </Button>
                        <Button
                            minW="150px"
                            leftIcon={<AddIcon />}
                            onClick={handleAddAOR}
                            size="sm">
                            Add Registration
                        </Button>

                        {/* Registration Filter Dropdown */}
                        <FormControl className="!ml-[16px]">
                            <HStack
                                justifyContent="center"
                                w="500px"
                                sx={tooltipChildrenStyles}>
                                <FormLabel
                                    className="w-1/6 leading-4"
                                    fontSize="sm"
                                    mb={0}
                                    mr={0}>
                                    Filter by Registrar:
                                </FormLabel>
                                <Tooltip
                                    label={tooltipSelectLabel}
                                    shouldWrapChildren>
                                    <Select
                                        className="w-full"
                                        placeholder={
                                            selectedRegistrarPlaceholder
                                        }
                                        isLoading={registrarsLoading}
                                        value={selectedRegistrarValue}
                                        options={registrars}
                                        getOptionLabel={(option) => option.name}
                                        getOptionValue={(option) => option.id}
                                        onChange={(value) =>
                                            handleRegistrarChange(value)
                                        }
                                        isClearable={false}
                                        chakraStyles={dropDownFormStyle(
                                            darkMode,
                                        )}
                                        selectedOptionColor="brand"
                                        isDisabled={isSelectedDisabled}
                                    />
                                </Tooltip>
                            </HStack>
                        </FormControl>
                    </HStack>
                }
            />

            <LogsModal
                isOpen={isOpenLogsModal}
                onClose={handleCloseLogsModal}
                aorId={selectedAORForLogs}
                extensionName={extensionName}
            />
        </>
    );
}
